<template>
  <v-card class="elevation-0">
    <v-card-text class="pa-0">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-row>
          <v-col cols="12" md="8" lg="6">
            <!-- Company Information Section -->
            <div class="mb-6">
              <h3 class="text-h6 mb-4">
                <v-icon left small>mdi-office-building</v-icon>
                {{ $t('setting.sections.company_info') || 'Company Information' }}
              </h3>

              <v-text-field
                v-model="form.name"
                :label="$t('setting.fields.company_name') || 'Company Name'"
                outlined
                dense
                disabled
                prepend-inner-icon="mdi-domain"
                class="mb-3"
              />

              <v-text-field
                v-model="form.address"
                :label="$t('setting.fields.address') || 'Address'"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker"
                :rules="[v => !!v || 'Address is required']"
                class="mb-3"
              />

              <v-text-field
                v-model="form.address2"
                :label="$t('setting.fields.other_address') || 'Other Address'"
                outlined
                dense
                prepend-inner-icon="mdi-map-marker-plus"
                class="mb-3"
              />
            </div>

            <!-- Contact Information Section -->
            <div class="mb-6">
              <h3 class="text-h6 mb-4">
                <v-icon left small>mdi-contacts</v-icon>
                {{ $t('setting.sections.contact_info') || 'Contact Information' }}
              </h3>

              <v-text-field
                v-model="form.phone"
                :label="$t('setting.fields.phone') || 'Phone'"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
                :rules="phoneRules"
                class="mb-3"
              />

              <v-text-field
                v-model="form.email"
                :label="$t('setting.fields.email') || 'E-mail'"
                outlined
                dense
                prepend-inner-icon="mdi-email"
                :rules="emailRules"
                class="mb-3"
              />

              <v-text-field
                v-model="form.website"
                :label="$t('setting.fields.website') || 'Website'"
                outlined
                dense
                prepend-inner-icon="mdi-web"
                :rules="websiteRules"
                class="mb-3"
              />
            </div>

            <!-- Payroll Section -->
            <div class="mb-6">
              <h3 class="text-h6 mb-4">
                <v-icon left small>mdi-calendar-clock</v-icon>
                {{ $t('setting.sections.payroll') || 'Payroll Settings' }}
              </h3>

              <v-menu
                ref="dateMenu"
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formattedDate"
                    :label="$t('setting.fields.salary_payment_date') || 'Salary Payment Date'"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    hint="Select the day of month for salary payments"
                    persistent-hint
                  />
                </template>
                <v-date-picker
                  v-model="form.salary_payment"
                  @input="dateMenu = false"
                  :format="formatDate"
                />
              </v-menu>
            </div>

            <!-- Action Buttons -->
            <v-divider class="mb-4" />

            <div class="d-flex justify-end">
              <v-btn
                text
                class="mr-2"
                @click="loadSettings"
                :disabled="loading"
              >
                <v-icon left>mdi-refresh</v-icon>
                {{ $t('button.reset') || 'Reset' }}
              </v-btn>

              <v-btn
                color="primary"
                @click="update"
                :loading="loading"
                :disabled="!valid || loading"
                v-can.disable="'setting.organisation'"
              >
                <v-icon left>mdi-content-save</v-icon>
                {{ $t('button.save') || 'Save Changes' }}
              </v-btn>
            </div>
          </v-col>

          <!-- Help Section -->
          <v-col cols="12" md="4" lg="6" class="pl-md-6">
            <v-alert
              type="info"
              text
              dense
              border="left"
              class="mb-4"
            >
              <div class="text-subtitle-2 font-weight-medium mb-2">
                {{ $t('setting.help.organisation_title') || 'About Organisation Settings' }}
              </div>
              <div class="text-caption">
                {{ $t('setting.help.organisation_desc') || 'These settings define your company\'s basic information that appears on documents, receipts, and reports.' }}
              </div>
            </v-alert>

            <v-alert
              type="warning"
              text
              dense
              border="left"
            >
              <div class="text-subtitle-2 font-weight-medium mb-2">
                {{ $t('setting.help.company_name_locked') || 'Company Name Locked' }}
              </div>
              <div class="text-caption">
                {{ $t('setting.help.company_name_locked_desc') || 'The company name cannot be changed after initial setup. Contact support if you need to modify it.' }}
              </div>
            </v-alert>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      color="success"
      bottom
      right
    >
      <v-icon left>mdi-check-circle</v-icon>
      {{ snackbarText }}
    </v-snackbar>
  </v-card>
</template>

<script>
export default {
  name: 'Organisation',
  data () {
    return {
      form: {
        name: '',
        address: '',
        address2: '',
        email: '',
        website: '',
        phone: '',
        salary_payment: ''
      },
      loading: false,
      valid: true,
      dateMenu: false,
      snackbar: false,
      snackbarText: '',
      // Validation rules
      emailRules: [
        v => !!v || 'E-mail is required',
        v => /.+@.+\..+/.test(v) || 'E-mail must be valid'
      ],
      phoneRules: [
        v => !!v || 'Phone is required',
        v => /^[\d\s\-+()]+$/.test(v) || 'Phone must be valid'
      ],
      websiteRules: [
        v => !v || /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(v) || 'Website must be valid URL'
      ]
    }
  },
  computed: {
    formattedDate () {
      if (!this.form.salary_payment) return ''
      const date = new Date(this.form.salary_payment)
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long' })
    }
  },
  methods: {
    formatDate (date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      return `${day}/${month}`
    },
    async update () {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true
      try {
        await this.$configService.setConfig(this.form, 'org')
        this.snackbarText = this.$t('status.success') || 'Settings updated successfully'
        this.snackbar = true

        // Emit success event to parent
        this.$emit('parent-description', 'Company information has been updated')
      } catch (error) {
        this.globalErrHandle(error)
      } finally {
        this.loading = false
      }
    },
    async loadSettings () {
      this.loading = true
      try {
        const data = await this.$configService.getConfig()
        this.form = {
          name: data.company_name || '',
          address: data.company_address || '',
          address2: data.company_other_address || '',
          email: data.company_email || '',
          website: data.company_website || '',
          phone: data.company_phone || '',
          salary_payment: data.salary_payment || ''
        }
      } catch (error) {
        this.globalErrHandle(error)
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    // Emit title and description to parent
    this.$emit('parent-title', this.$t('setting.tabs.organisation') || 'Organisation')
    this.$emit('parent-description', this.$t('setting.desc.organisation_full') || 'Manage your company information, contact details, and payroll settings')

    // Load initial settings
    this.loadSettings()
  }
}
</script>

<style scoped>
/* Remove default card shadow for seamless integration */
.v-card {
  background: transparent;
}

/* Ensure proper spacing on mobile */
@media (max-width: 959px) {
  .pl-md-6 {
    padding-left: 0 !important;
    padding-top: 24px;
  }
}
</style>
